<template>
  <div
    class="fixed inset-y-0 right-0 w-[420px] max-w-[90vw] z-[1050] flex flex-col bg-slate-900/95 backdrop-blur-xl border-l border-slate-700/60 shadow-2xl transform transition-transform duration-300"
    :class="visible ? 'translate-x-0' : 'translate-x-full'"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-5 py-4 border-b border-slate-700/50">
      <div>
        <h3 class="text-sm font-semibold text-white">
          Checkout Notifications
        </h3>
        <p v-if="checkoutLabel" class="text-[11px] text-slate-400 mt-0.5">
          {{ checkoutLabel }}
        </p>
      </div>
      <button
        @click="emit('close')"
        class="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Tab bar -->
    <div class="flex border-b border-slate-700/50">
      <button
        @click="activeTab = 'timeline'"
        class="flex-1 px-4 py-2.5 text-xs font-medium transition-colors relative"
        :class="activeTab === 'timeline'
          ? 'text-white'
          : 'text-slate-400 hover:text-slate-200'"
      >
        Timeline
        <div
          v-if="activeTab === 'timeline'"
          class="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-500 rounded-full"
        ></div>
      </button>
      <button
        @click="activeTab = 'replies'"
        class="flex-1 px-4 py-2.5 text-xs font-medium transition-colors relative"
        :class="activeTab === 'replies'
          ? 'text-white'
          : 'text-slate-400 hover:text-slate-200'"
      >
        Replies
        <span
          v-if="unreadReplies > 0"
          class="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[9px] font-semibold text-white bg-red-500 rounded-full min-w-[16px]"
        >
          {{ unreadReplies }}
        </span>
        <div
          v-if="activeTab === 'replies'"
          class="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-500 rounded-full"
        ></div>
      </button>
    </div>

    <!-- Tab content -->
    <div class="flex-1 overflow-y-auto px-5 py-4">
      <AlertsTimeline
        v-if="activeTab === 'timeline' && checkoutId"
        :checkout-id="checkoutId"
      />
      <InboxPanel
        v-if="activeTab === 'replies' && checkoutId"
        :checkout-id="checkoutId"
        :sender-name="senderName"
        @unread-change="onUnreadChange"
      />
    </div>
  </div>

  <!-- Backdrop -->
  <Transition name="fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-[1045] bg-black/40"
      @click="emit('close')"
    ></div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import AlertsTimeline from '@/components/AlertsTimeline.vue'
import InboxPanel from '@/components/InboxPanel.vue'

const props = defineProps<{
  visible: boolean
  checkoutId: number | null
  /** Display label for the checkout (e.g. equipment name + serial). */
  checkoutLabel?: string
  /** Name of the current user for reply emails. */
  senderName?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const activeTab = ref<'timeline' | 'replies'>('timeline')
const unreadReplies = ref(0)

function onUnreadChange(count: number) {
  unreadReplies.value = count
}

// Reset tab when panel opens with new checkout
watch(() => props.checkoutId, () => {
  activeTab.value = 'timeline'
  unreadReplies.value = 0
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
